import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DictableTextarea from '@/components/DictableTextarea';
import ConsegnaVocale from '@/components/ConsegnaVocale';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Printer, AlertTriangle, Pill, FileText, Activity, CalendarCheck, BedDouble,
  UserPlus, Clock, Zap, Save, Trash2, RefreshCw, Users, CalendarClock, HeartPulse, PenLine, Mail, CheckCircle2
} from 'lucide-react';
import { openPrintWindow } from '@/lib/letterhead';
import { useSede } from '@/lib/sede';
import DatePickerField from '@/components/DatePickerField';

const TURNI = {
  mattina: { label: 'Mattina', orari: '06:00 – 13:59' },
  pomeriggio: { label: 'Pomeriggio', orari: '14:00 – 21:59' },
  notte: { label: 'Notte', orari: '22:00 – 05:59' },
};

const turnoCorrente = () => {
  const h = new Date().getHours();
  if (h >= 6 && h < 14) return 'mattina';
  if (h >= 14 && h < 22) return 'pomeriggio';
  return 'notte';
};

const STATO_SOMM = { non_somministrato: 'Non somministrato', rifiutato: 'Rifiutato', non_disponibile: 'Non disponibile' };

const Section = ({ icon: Icon, color, title, count, children, testid }) => (
  <Card data-testid={testid}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        {title}
        {count > 0 && <Badge variant="outline" className="text-[10px] ml-auto">{count}</Badge>}
      </CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const Empty = ({ text }) => <p className="text-xs text-slate-400 py-4 text-center">{text}</p>;

export default function ConsegnaTurno() {
  const [area, setArea] = useState('tutte');
  const [turno, setTurno] = useState(turnoCorrente());
  const [giorno, setGiorno] = useState(new Date().toISOString().slice(0, 10));
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nota, setNota] = useState('');
  const [criticita, setCriticita] = useState(false);
  const [saving, setSaving] = useState(false);
  const [firme, setFirme] = useState([]);
  const [firmando, setFirmando] = useState(false);
  const [inviando, setInviando] = useState(false);
  const { sedeAttiva } = useSede();

  const loadFirme = useCallback(async () => {
    try { const r = await api.get('/consegna/firme', { params: { data: giorno } }); setFirme(r.data); }
    catch { /* no-op */ }
  }, [giorno]);

  const firmaConsegna = async () => {
    setFirmando(true);
    try {
      const payload = { data: giorno, turno, area: area === 'tutte' ? '' : area, note: nota };
      if (sedeAttiva && sedeAttiva !== 'tutte') payload.sede_id = sedeAttiva;
      const r = await api.post('/consegna/firma', payload);
      toast.success(`Consegna firmata da ${r.data.firmato_da}`);
      loadFirme();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore firma consegna'); }
    setFirmando(false);
  };

  const inviaEmail = async () => {
    setInviando(true);
    try {
      const payload = { data: giorno, turno, area: area === 'tutte' ? '' : area };
      if (sedeAttiva && sedeAttiva !== 'tutte') payload.sede_id = sedeAttiva;
      const r = await api.post('/consegna/invia-email', payload);
      if (r.data.avviso) toast.warning(r.data.avviso);
      else toast.success(`Consegna inviata a ${r.data.inviate.length} destinatari`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invio email'); }
    setInviando(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/consegna/turno', { params: { data: giorno, turno, area } });
      setD(res.data);
    } catch {
      setD(null);
      toast.error('Errore nel caricamento della consegna');
    }
    setLoading(false);
  }, [giorno, turno, area]);

  useEffect(() => { load(); loadFirme(); }, [load, loadFirme]);

  const salvaNota = async () => {
    if (!nota.trim()) { toast.error('Scrivi la nota di consegna'); return; }
    setSaving(true);
    try {
      await api.post('/consegna/note', { data: giorno, turno, area, contenuto: nota, criticita });
      setNota(''); setCriticita(false);
      toast.success('Nota di consegna registrata');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSaving(false);
  };

  const eliminaNota = async (id) => {
    try {
      await api.delete(`/consegna/note/${id}`);
      toast.success('Nota eliminata');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handlePrint = () => {
    if (!d) return;
    const areaLabel = d.area === 'tutte' ? 'Tutte le Aree' : `Area ${d.area.charAt(0).toUpperCase() + d.area.slice(1)}`;
    const r = d.riepilogo;
    const dataLabel = new Date(d.data).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    let body = '';

    body += `<div class="info-grid">
      <div class="info-item"><span class="info-label">Data</span> <span class="info-value">${dataLabel}</span></div>
      <div class="info-item"><span class="info-label">Turno</span> <span class="info-value">${TURNI[d.turno].label} (${TURNI[d.turno].orari})</span></div>
      <div class="info-item"><span class="info-label">Area</span> <span class="info-value">${areaLabel}</span></div>
      <div class="info-item"><span class="info-label">Ospiti presenti</span> <span class="info-value">${r.ospiti_presenti}</span></div>
      <div class="info-item"><span class="info-label">Ricoverati</span> <span class="info-value">${r.ricoverati}</span></div>
      <div class="info-item"><span class="info-label">Nuovi ingressi (7gg)</span> <span class="info-value">${r.nuovi_ingressi}</span></div>
      <div class="info-item"><span class="info-label">Somministrazioni turno</span> <span class="info-value">${r.somministrazioni_effettuate} effettuate / ${r.somministrazioni_mancate} mancate</span></div>
      <div class="info-item"><span class="info-label">Compilato da</span> <span class="info-value">${d.generato_da}</span></div>
    </div>`;

    const table = (title, headers, rows) => {
      if (!rows.length) return '';
      let h = `<p class="section-title">${title}</p><table><thead><tr>${headers.map(x => `<th>${x}</th>`).join('')}</tr></thead><tbody>`;
      h += rows.map(cells => `<tr>${cells.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`).join('');
      return h + `</tbody></table>`;
    };

    body += table('Criticita e Note di Consegna', ['Ora', 'Operatore', 'Nota'],
      d.note.map(n => [new Date(n.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
        n.operatore_nome, `${n.criticita ? '<span class="badge badge-red">CRITICO</span> ' : ''}${n.contenuto}`]));

    body += table('Eventi Avversi (ultime 24h)', ['Data', 'Ospite', 'Tipo', 'Dinamica', 'Interventi'],
      d.eventi.map(e => [e.data_ora ? new Date(e.data_ora).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '',
        e.ospite_nome, `<span class="badge badge-red">${e.tipo_label}</span>`, e.dinamica, e.interventi]));

    body += table('Somministrazioni Non Effettuate', ['Ospite', 'Camera', 'Ora', 'Stato', 'Motivo'],
      d.somministrazioni_mancate.map(s => [s.ospite_nome, s.stanza, s.orario,
        `<span class="badge badge-amber">${STATO_SOMM[s.stato] || s.stato}</span>`, s.note]));

    body += table('Terapie Al Bisogno Somministrate', ['Ospite', 'Camera', 'Ora', 'Farmaco', 'Note'],
      d.al_bisogno.map(s => [s.ospite_nome, s.stanza, s.orario, s.farmaco, s.note]));

    body += table('Parametri Fuori Range (24h)', ['Ospite', 'Camera', 'Parametro', 'Valore', 'Operatore'],
      d.parametri.map(p => [p.ospite_nome, p.stanza, p.tipo,
        `<span style="color:#dc2626;font-weight:700">${p.valore} ${p.unita || ''}</span>`, p.operatore_nome]));

    body += table('Note Diario Clinico del Turno', ['Ospite', 'Camera', 'Contenuto', 'Autore'],
      d.diario.slice(0, 40).map(x => [x.ospite_nome, x.stanza, x.contenuto, x.autore]));

    body += table('Appuntamenti Oggi e Domani', ['Quando', 'Data/Ora', 'Ospite', 'Tipo', 'Luogo', 'Stato'],
      d.appuntamenti.map(a => [`<span class="badge ${a.quando === 'oggi' ? 'badge-red' : 'badge-blue'}">${a.quando}</span>`,
        a.data_ora ? new Date(a.data_ora).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '',
        a.ospite_nome, a.tipo, a.luogo, a.stato]));

    body += table('Ospiti Ricoverati in Ospedale', ['Ospite', 'Camera', 'Area'],
      d.ricoverati.map(o => [o.ospite_nome, o.stanza, o.area]));

    body += table('Nuovi Ingressi (ultimi 7 giorni)', ['Ospite', 'Camera', 'Letto', 'Data ingresso'],
      d.nuovi_ingressi.map(o => [o.ospite_nome, o.stanza, o.letto, o.data_ingresso]));

    body += table('Farmaci in Esaurimento', ['Ospite', 'Farmaco', 'Scorta', 'Giorni'],
      d.farmaci_esaurimento.slice(0, 15).map(f => [f.ospite_nome, `${f.farmaco} ${f.dosaggio}`, f.scorta_attuale,
        `<span class="badge ${f.giorni_rimasti <= 3 ? 'badge-red' : 'badge-amber'}">${f.giorni_rimasti} gg</span>`]));

    body += table('Alert ECG da Refertare', ['Ospite', 'Camera', 'Data ECG', 'Priorita', 'Motivo'],
      (d.ecg_alert || []).map(e => [e.ospite_nome, e.stanza, e.data_esame,
        `<span class="badge ${e.alert_livello === 'alta' ? 'badge-red' : 'badge-amber'}">${e.alert_livello}</span>`,
        (e.alert_motivi || []).join('; ')]));

    body += table('Scadenze Imminenti (7 giorni)', ['Tipo', 'Ospite', 'Dettaglio', 'Scadenza'],
      [...d.pai_scadenze.map(p => ['Rivalutazione PAI', p.ospite_nome, p.scaduto ? '<span class="badge badge-red">SCADUTO</span>' : '', p.data_rivalutazione]),
       ...d.terapie_scadenza.map(t => ['Fine terapia', t.ospite_nome, `${t.farmaco} ${t.dosaggio}`, t.durata_fine])]);

    body += `<div class="signature-row"><div class="signature-block">Consegnante</div><div class="signature-block">Ricevente</div><div class="signature-block">Data/Ora</div></div>`;

    openPrintWindow('Consegna Turno', body, { subtitle: `${TURNI[d.turno].label} - ${areaLabel} - ${dataLabel}` });
  };

  const r = d?.riepilogo;

  return (
    <div className="space-y-5 max-w-[1600px] mx-auto" data-testid="consegna-turno-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Consegna Turno</h1>
          <p className="text-sm text-slate-500">Report clinico centralizzato e stampabile per il cambio turno</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DatePickerField value={giorno} onChange={v => setGiorno(v)} className="w-40" testId="consegna-data-input" />
          <Select value={turno} onValueChange={setTurno}>
            <SelectTrigger className="w-36" data-testid="consegna-turno-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TURNI).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger className="w-36" data-testid="consegna-area-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutte">Tutte le aree</SelectItem>
              <SelectItem value="verde">Area Verde</SelectItem>
              <SelectItem value="gialla">Area Gialla</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="consegna-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={handlePrint} data-testid="print-consegna-btn"><Printer className="w-4 h-4 mr-1" />Stampa Consegna</Button>
          <Button variant="outline" onClick={firmaConsegna} disabled={firmando} data-testid="firma-consegna-btn">
            <PenLine className="w-4 h-4 mr-1" />{firmando ? 'Firma...' : 'Chiudi e firma'}
          </Button>
          <Button variant="outline" onClick={inviaEmail} disabled={inviando} data-testid="invia-consegna-btn">
            <Mail className="w-4 h-4 mr-1" />{inviando ? 'Invio...' : 'Invia per email'}
          </Button>
        </div>
      </div>

      {firme.length > 0 && (
        <div className="rounded-lg border border-brand/30 bg-brand-soft/40 p-3 flex items-start gap-2" data-testid="firme-banner">
          <CheckCircle2 className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
          <div className="text-xs text-brand-dark">
            {firme.map(f => (
              <p key={f.id}>
                Consegna <strong>{f.turno}</strong> firmata da <strong>{f.firmato_da}</strong> alle {f.firmato_il.slice(11, 16)}
                {f.note ? ` · ${f.note}` : ''}
              </p>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-400">Caricamento...</div>
      ) : !d ? (
        <div className="text-center py-12 text-slate-400">Nessun dato</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="consegna-riepilogo">
            {[
              { l: 'Presenti', v: r.ospiti_presenti, i: Users, c: 'text-emerald-600' },
              { l: 'Ricoverati', v: r.ricoverati, i: BedDouble, c: 'text-indigo-600' },
              { l: 'Nuovi ingressi', v: r.nuovi_ingressi, i: UserPlus, c: 'text-brand' },
              { l: 'Eventi 24h', v: r.eventi_avversi, i: AlertTriangle, c: 'text-red-600' },
              { l: 'Somm. mancate', v: r.somministrazioni_mancate, i: Pill, c: 'text-amber-600' },
              { l: 'Appuntamenti', v: r.appuntamenti, i: CalendarCheck, c: 'text-cyan-600' },
              { l: 'Alert ECG', v: r.ecg_alert || 0, i: HeartPulse, c: 'text-rose-600' },
              { l: 'Scadenze 7gg', v: r.scadenze, i: CalendarClock, c: 'text-orange-600' },
            ].map(k => (
              <div key={k.l} className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 hover:shadow-sm transition-shadow">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400"><k.i className={`w-3.5 h-3.5 ${k.c}`} />{k.l}</div>
                <p className="text-2xl font-bold text-slate-900 leading-tight mt-0.5">{k.v}</p>
              </div>
            ))}
          </div>

          <Card data-testid="consegna-note-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-slate-500" />Note di Consegna - {TURNI[turno].label}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <DictableTextarea
                value={nota} onChange={e => setNota(e.target.value)} rows={3}
                contesto="Consegna di fine turno in RSA" 
                placeholder="Consegna al turno successivo: criticita, indicazioni mediche, attivita in sospeso... (usa il microfono per dettarla a voce)"
                data-testid="consegna-nota-input"
              />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <Checkbox checked={criticita} onCheckedChange={v => setCriticita(!!v)} data-testid="consegna-criticita-check" />
                  Segnala come criticita
                </label>
                <div className="flex items-center gap-2">
                <ConsegnaVocale giorno={giorno} turno={turno} area={area} onSaved={load} />
                <Button size="sm" onClick={salvaNota} disabled={saving} data-testid="consegna-salva-nota-btn">
                  <Save className="w-4 h-4 mr-1" />{saving ? 'Salvataggio...' : 'Registra nota'}
                </Button>
                </div>
              </div>
              {d.note.length === 0 ? <Empty text="Nessuna nota registrata per questo turno" /> : (
                <div className="space-y-2" data-testid="consegna-note-list">
                  {d.note.map(n => (
                    <div key={n.id} className={`p-2.5 rounded-lg border text-xs ${n.criticita ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`consegna-nota-${n.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-slate-800">{n.operatore_nome}</span>
                        <div className="flex items-center gap-2">
                          {n.criticita && <Badge className="bg-red-100 text-red-700 text-[9px]">CRITICITA</Badge>}
                          <span className="text-slate-400">{new Date(n.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                          <button onClick={() => eliminaNota(n.id)} className="text-slate-400 hover:text-red-600" data-testid={`consegna-elimina-nota-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                      <p className="text-slate-600 mt-1 whitespace-pre-wrap">{n.contenuto}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section icon={AlertTriangle} color="text-red-500" title="Eventi Avversi (24h)" count={d.eventi.length} testid="card-eventi">
              {d.eventi.length === 0 ? <Empty text="Nessun evento" /> : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">{d.eventi.map((e, i) => (
                  <div key={i} className="p-2 rounded-lg bg-red-50 border border-red-100 text-xs">
                    <div className="flex justify-between gap-2"><span className="font-medium">{e.ospite_nome}{e.stanza && <span className="text-slate-400 font-normal"> · cam. {e.stanza}</span>}</span><Badge className="text-[9px] bg-red-100 text-red-700">{e.tipo_label}</Badge></div>
                    <p className="text-slate-600 mt-1">{e.dinamica}</p>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section icon={Pill} color="text-amber-500" title="Somministrazioni Non Effettuate" count={d.somministrazioni_mancate.length} testid="card-somm-mancate">
              {d.somministrazioni_mancate.length === 0 ? <Empty text="Tutte le somministrazioni regolari" /> : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">{d.somministrazioni_mancate.map((s, i) => (
                  <div key={i} className="p-2 rounded-lg bg-amber-50 border border-amber-100 text-xs">
                    <div className="flex justify-between gap-2"><span className="font-medium">{s.ospite_nome}</span><Badge className="text-[9px] bg-amber-100 text-amber-700">{STATO_SOMM[s.stato] || s.stato}</Badge></div>
                    <p className="text-slate-500 mt-0.5">ore {s.orario}{s.note && ` · ${s.note}`}</p>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section icon={Zap} color="text-violet-500" title="Terapie Al Bisogno Somministrate" count={d.al_bisogno.length} testid="card-al-bisogno">
              {d.al_bisogno.length === 0 ? <Empty text="Nessuna terapia al bisogno" /> : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">{d.al_bisogno.map((s, i) => (
                  <div key={i} className="p-2 rounded-lg bg-violet-50 border border-violet-100 text-xs flex justify-between gap-2">
                    <div><span className="font-medium">{s.ospite_nome}</span><span className="text-slate-500"> · {s.farmaco}</span>{s.note && <p className="text-slate-500">{s.note}</p>}</div>
                    <span className="text-slate-400 whitespace-nowrap">{s.orario}</span>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section icon={Activity} color="text-blue-500" title="Parametri Fuori Range (24h)" count={d.parametri.length} testid="card-parametri">
              {d.parametri.length === 0 ? <Empty text="Tutti nella norma" /> : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">{d.parametri.map((p, i) => (
                  <div key={i} className="p-2 rounded-lg bg-blue-50 border border-blue-100 text-xs flex justify-between">
                    <span>{p.ospite_nome} · {p.tipo}</span>
                    <span className="font-bold text-red-600">{p.valore} {p.unita}</span>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section icon={FileText} color="text-emerald-500" title={`Note Diario - ${TURNI[turno].label}`} count={d.diario.length} testid="card-diario">
              {d.diario.length === 0 ? <Empty text="Nessuna nota" /> : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">{d.diario.slice(0, 25).map((x, i) => (
                  <div key={i} className="p-2 rounded-lg bg-slate-50 border border-slate-100 text-xs">
                    <div className="flex justify-between gap-2"><span className="font-medium">{x.ospite_nome}</span><span className="text-slate-400">{x.autore}</span></div>
                    <p className="text-slate-600 mt-1">{x.contenuto}</p>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section icon={CalendarCheck} color="text-cyan-500" title="Appuntamenti Oggi e Domani" count={d.appuntamenti.length} testid="card-appuntamenti">
              {d.appuntamenti.length === 0 ? <Empty text="Nessun appuntamento" /> : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">{d.appuntamenti.map((a, i) => (
                  <div key={i} className="p-2 rounded-lg bg-cyan-50 border border-cyan-100 text-xs">
                    <div className="flex justify-between gap-2">
                      <span className="font-medium">{a.ospite_nome}</span>
                      <Badge className={`text-[9px] ${a.quando === 'oggi' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{a.quando}</Badge>
                    </div>
                    <p className="text-slate-600 mt-0.5 capitalize">{a.tipo} · {a.data_ora ? new Date(a.data_ora).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}{a.luogo && ` · ${a.luogo}`}</p>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section icon={BedDouble} color="text-indigo-500" title="Ricoveri e Nuovi Ingressi" count={d.ricoverati.length + d.nuovi_ingressi.length} testid="card-movimenti">
              {d.ricoverati.length + d.nuovi_ingressi.length === 0 ? <Empty text="Nessun movimento" /> : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {d.ricoverati.map((o, i) => (
                    <div key={`r${i}`} className="p-2 rounded-lg bg-indigo-50 border border-indigo-100 text-xs flex justify-between">
                      <span className="font-medium">{o.ospite_nome}</span><Badge className="text-[9px] bg-indigo-100 text-indigo-700">ricoverato</Badge>
                    </div>
                  ))}
                  {d.nuovi_ingressi.map((o, i) => (
                    <div key={`n${i}`} className="p-2 rounded-lg bg-emerald-50 border border-emerald-100 text-xs flex justify-between">
                      <span className="font-medium">{o.ospite_nome} <span className="text-slate-400 font-normal">cam. {o.stanza} {o.letto}</span></span>
                      <Badge className="text-[9px] bg-emerald-100 text-emerald-700">ingresso {o.data_ingresso}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={HeartPulse} color="text-rose-500" title="Alert ECG da refertare" count={d.ecg_alert?.length || 0} testid="card-ecg-alert">
              {(d.ecg_alert || []).length === 0 ? <Empty text="Nessun alert ECG aperto" /> : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">{d.ecg_alert.map((e, i) => (
                  <div key={i} className={`p-2 rounded-lg border text-xs ${e.alert_livello === 'alta' ? 'bg-red-50 border-red-100' : 'bg-amber-50 border-amber-100'}`}>
                    <div className="flex justify-between gap-2">
                      <span className="font-medium">{e.ospite_nome}{e.stanza && <span className="text-slate-400 font-normal"> · cam. {e.stanza}</span>}</span>
                      <Badge className={`text-[9px] ${e.alert_livello === 'alta' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{e.alert_livello}</Badge>
                    </div>
                    <p className="text-slate-600 mt-0.5">ECG {e.data_esame} · {(e.alert_motivi || [])[0]}</p>
                  </div>
                ))}</div>
              )}
            </Section>

            <Section icon={Clock} color="text-orange-500" title="Scadenze e Scorte" count={r.scadenze + d.farmaci_esaurimento.length} testid="card-scadenze">
              {r.scadenze + d.farmaci_esaurimento.length === 0 ? <Empty text="Nessuna scadenza imminente" /> : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {d.pai_scadenze.map((p, i) => (
                    <div key={`p${i}`} className={`p-2 rounded-lg border text-xs flex justify-between ${p.scaduto ? 'bg-red-50 border-red-100' : 'bg-orange-50 border-orange-100'}`}>
                      <span>{p.ospite_nome} · Rivalutazione PAI</span>
                      <span className={p.scaduto ? 'font-bold text-red-600' : 'text-orange-700'}>{p.data_rivalutazione}</span>
                    </div>
                  ))}
                  {d.terapie_scadenza.map((t, i) => (
                    <div key={`t${i}`} className="p-2 rounded-lg bg-orange-50 border border-orange-100 text-xs flex justify-between">
                      <span>{t.ospite_nome} · fine {t.farmaco}</span><span className="text-orange-700">{t.durata_fine}</span>
                    </div>
                  ))}
                  {d.farmaci_esaurimento.slice(0, 10).map((f, i) => (
                    <div key={`f${i}`} className="p-2 rounded-lg bg-red-50 border border-red-100 text-xs flex justify-between">
                      <span>{f.ospite_nome} · {f.farmaco}</span>
                      <Badge className={`text-[9px] ${f.giorni_rimasti <= 3 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{f.giorni_rimasti} gg</Badge>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
